import { Plus, SearchX } from "lucide-react";
import { motion } from "motion/react";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useParams, useSearchParams } from "react-router-dom";

import { SegmentDetail, SegmentDetailSkeleton } from "./components/SegmentDetail";
import { SegmentDetailEmpty } from "./components/SegmentDetailEmpty";
import { SegmentList } from "./components/SegmentList";
import { SegmentListHeader } from "./components/SegmentListHeader";
import { SEGMENT_DETAIL_PARAM } from "./constants";

import EmptyState from "@/components/ui/v2/empty-state";
import { ROUTES } from "@/constants/routes";
import { useSegmentDetail } from "@/features/segments/api/useSegmentDetail";
import { useSegmentsInfinite } from "@/features/segments/api/useSegmentsInfinite";
import type { SegmentStatus } from "@/features/segments/constants";
import { getOverviewTourStepIds, SEGMENT_OVERVIEW_TOUR } from "@/features/segments/tour/segmentOverviewTour";
import { SegmentOverviewTourHost } from "@/features/segments/tour/SegmentOverviewTourHost";
import { SegmentTourBanner } from "@/features/segments/tour/SegmentTourBanner";
import { TourEntry } from "@/features/tours/components/TourEntry";
import { TourProvider } from "@/features/tours/TourProvider";
import { usePermissionMatrix } from "@/lib/hooks/usePermissionMatrix";
import { useSearchSearchParams } from "@/lib/hooks/useSearchSearchParams";
import { useSortSearchParams } from "@/lib/hooks/useSortSearchParams";
import { cn } from "@/lib/utils/cn";

const OverviewSegmentsContent = () => {
  const { clientId } = useParams();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canCreate } = usePermissionMatrix();
  const { orderBy, descending } = useSortSearchParams();
  const { searchValue, debouncedSearchValue, setSearchValue } = useSearchSearchParams();

  const selectedId = searchParams.get(SEGMENT_DETAIL_PARAM) ?? undefined;

  const {
    data: segmentsPages,
    isLoading: segmentsLoading,
    isFetching: segmentsFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useSegmentsInfinite({
    clientId,
    descending,
    orderBy: orderBy ?? "name",
    search: debouncedSearchValue,
    pageSize: 10,
  });

  const segments = useMemo(() => segmentsPages?.pages.flatMap((p) => p.data) ?? [], [segmentsPages]);
  const totalSegments = segmentsPages?.pages[0]?.totalRecords ?? 0;

  const {
    data: segmentDetail,
    isLoading: detailLoading,
  } = useSegmentDetail({
    segmentId: selectedId,
    clientId,
  });

  const isSelectedArchived = segmentDetail?.archivedAt != null;

  useEffect(() => {
    if (!selectedId) return;
    const isMissingFromList =
      !segmentsLoading && !hasNextPage && !segments.some((s) => s.id === selectedId);
    if (!isSelectedArchived && !isMissingFromList) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete(SEGMENT_DETAIL_PARAM);
      return next;
    });
  }, [selectedId, isSelectedArchived, segments, segmentsLoading, hasNextPage, setSearchParams]);

  const handleSelect = useCallback(
    (id: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (prev.get(SEGMENT_DETAIL_PARAM) === id) {
          next.delete(SEGMENT_DETAIL_PARAM);
        } else {
          next.set(SEGMENT_DETAIL_PARAM, id);
        }
        return next;
      });
    },
    [setSearchParams],
  );

  const selectSegment = useCallback(
    (id: string | undefined) =>
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (id) {
          next.set(SEGMENT_DETAIL_PARAM, id);
        } else {
          next.delete(SEGMENT_DETAIL_PARAM);
        }
        return next;
      }),
    [setSearchParams],
  );

  const handleToggleSort = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("descending", String(!descending));
      if (!orderBy) next.set("orderBy", "name");
      return next;
    });
  }, [setSearchParams, descending, orderBy]);

  const isEmpty = !segmentsLoading && segments.length === 0 && !debouncedSearchValue;

  if (isEmpty) {
    return (
      <div className="py-2">
        <SegmentTourBanner />
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="flex h-[60dvh] items-center justify-center"
        >
          <EmptyState
            icon={SearchX}
            title={t("common.noSegments", "No segments found")}
            description={canCreate("segments")
              ? t("common.segmentsNoDataDescription", "Get started by creating a segment.")
              : t("common.segmentsNoDataDescriptionReadonly", "There are no segments yet.")}
            action={canCreate("segments") ? {
              label: t("admin.segments.createSegment", "Create Segment"),
              href: ROUTES.new,
              icon: <Plus className="mr-1.5 size-4" />,
            } : undefined}
          />
        </motion.div>
        <Outlet />
      </div>
    );
  }

  const segmentListItems = segments.map((s) => ({
    id: s.id,
    name: s.name,
    status: s.status as SegmentStatus,
    lastUpdatedDate: s.updatedDate,
  }));

  return (
    <div className="grid gap-0 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
      <SegmentTourBanner />

      <div className={cn("lg:max-h-[calc(100dvh-10rem)] lg:overflow-y-auto pr-4 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden", selectedId && "hidden lg:block")}>
        <SegmentListHeader
          totalSegments={totalSegments}
          searchValue={searchValue ?? ""}
          onSearchChange={setSearchValue}
          descending={descending ?? false}
          onToggleSort={handleToggleSort}
        />
        <SegmentList
          segments={segmentListItems}
          selectedId={selectedId}
          onSelect={handleSelect}
          isLoading={segmentsLoading}
          isFetching={segmentsFetching}
          hasMore={hasNextPage ?? false}
          onLoadMore={fetchNextPage}
          isLoadingMore={isFetchingNextPage}
        />
      </div>

      <div className={cn("lg:max-h-[calc(100dvh-10rem)] overflow-y-auto py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:border-l lg:border-border lg:pl-6", !selectedId && "hidden lg:block")}>
        {!selectedId && <SegmentDetailEmpty />}
        {selectedId && detailLoading && <SegmentDetailSkeleton />}
        {selectedId && segmentDetail && !isSelectedArchived && <SegmentDetail segment={segmentDetail} />}
      </div>

      <TourEntry visitKey={clientId ?? SEGMENT_OVERVIEW_TOUR.id} />
      <SegmentOverviewTourHost
        selectedId={selectedId}
        firstSegmentId={segments[0]?.id}
        onSelect={selectSegment}
      />
      <Outlet />
    </div>
  );
};

const OverviewSegmentsPage = () => {
  const { canCreate, canEdit } = usePermissionMatrix();
  const canCreateSegments = canCreate("segments");
  const canEditSegments = canEdit("segments");
  const stepIds = useMemo(
    () => getOverviewTourStepIds({ canCreate: canCreateSegments, canEdit: canEditSegments }),
    [canCreateSegments, canEditSegments],
  );

  return (
    <TourProvider definition={SEGMENT_OVERVIEW_TOUR} stepIds={stepIds}>
      <OverviewSegmentsContent />
    </TourProvider>
  );
};

export default OverviewSegmentsPage;
